import { useEffect, useMemo, useState } from "react"

import { SIMULATOR_TYPES } from "@/constants/simulatorTypes"
import {
  FEATURE_FLAGS,
  useOrganizationFeatureFlagScope,
  usePostHydrationFeatureFlagValue,
} from "@/feature-flags"
import {
  Button,
  ChevronDownIcon,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SearchInput,
} from "@coval/ui"
import {
  ArrowLeftRight,
  Bot,
  Mail,
  MessageCircle,
  MessageSquare,
  Mic,
  Phone,
  PhoneOutgoing,
  Video,
  X,
  type LucideIcon,
} from "lucide-react"

import { SimulatorType } from "@/types/api/SimulatorType"
import { formatSimulatorType } from "@/utils/formatting/format-simulator-type"

export const AGENT_TYPE_ICONS: Partial<Record<SimulatorType, LucideIcon>> = {
  [SimulatorType.VOICE]: Phone,
  [SimulatorType.OUTBOUND_VOICE]: PhoneOutgoing,
  [SimulatorType.OPENAI_REALTIME]: Mic,
  [SimulatorType.GEMINI_REALTIME]: Mic,
  [SimulatorType.GROK_REALTIME]: Mic,
  [SimulatorType.DEEPGRAM_REALTIME]: Mic,
  [SimulatorType.WEBSOCKET]: ArrowLeftRight,
  [SimulatorType.SDK_WEBSOCKET]: ArrowLeftRight,
  [SimulatorType.TEXT]: MessageSquare,
  [SimulatorType.CHAT_A2A]: MessageSquare,
  [SimulatorType.SMS]: MessageCircle,
  [SimulatorType.EMAIL]: Mail,
  [SimulatorType.LIVEKIT]: Video,
  [SimulatorType.PIPECAT]: Bot,
}

interface AgentTypeOption {
  type: SimulatorType
  label: string
  description: string
}

interface AgentTypeGroup {
  title: string
  items: readonly AgentTypeOption[]
}

export const AGENT_TYPE_GROUPS: readonly AgentTypeGroup[] = SIMULATOR_TYPES.map(
  (group) => ({
    title: group.group,
    items: group.items.map((item) => ({
      type: item.id,
      label: item.name,
      description: item.description,
    })),
  }),
)

interface AgentTypeModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (type: SimulatorType) => void
  groups?: readonly AgentTypeGroup[]
  currentValue?: SimulatorType
  title?: string
}

/** Agent type groups the organization can pick, narrowed by a search query. */
function useAvailableAgentTypeGroups(
  groups: readonly AgentTypeGroup[],
  query = "",
) {
  const sdkWebsocketFlagValue = usePostHydrationFeatureFlagValue(
    FEATURE_FLAGS.SDK_WEBSOCKET_AGENT,
  )
  const { isReady: isOrganizationScopeReady } =
    useOrganizationFeatureFlagScope()
  const isSdkWebsocketEnabled =
    sdkWebsocketFlagValue === true && isOrganizationScopeReady

  return useMemo(() => {
    const availableGroups = groups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.type !== SimulatorType.SDK_WEBSOCKET || isSdkWebsocketEnabled,
        ),
      }))
      .filter((group) => group.items.length > 0)
    const q = query.trim().toLowerCase()
    if (!q) {
      return availableGroups
    }
    return availableGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            item.description.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.items.length > 0)
  }, [groups, isSdkWebsocketEnabled, query])
}

interface AgentTypeOptionGroupsProps {
  groups: readonly AgentTypeGroup[]
  onSelect: (type: SimulatorType) => void
  currentValue?: SimulatorType
}

function AgentTypeOptionGroups({
  groups,
  onSelect,
  currentValue,
}: AgentTypeOptionGroupsProps) {
  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.title}>
          <h3 className="mb-3 mt-2 text-sm font-semibold text-coval-text">
            {group.title}
          </h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {group.items.map((item) => {
              const Icon = AGENT_TYPE_ICONS[item.type]
              const isSelected = item.type === currentValue
              return (
                <button
                  key={item.type}
                  type="button"
                  data-testid={`agent-type-option-${item.type}`}
                  onClick={() => onSelect(item.type)}
                  className={cn(
                    "rounded-lg border p-3 text-left transition-colors",
                    isSelected
                      ? "border-coval-info-border bg-coval-info-surface hover:bg-coval-info-surface"
                      : "border-coval-border bg-surface-default hover:border-coval-info-border",
                  )}
                >
                  {Icon && (
                    <Icon
                      className={cn(
                        "mb-2 h-4 w-4",
                        isSelected
                          ? "text-coval-info-on-surface"
                          : "text-coval-text-muted",
                      )}
                    />
                  )}
                  <div
                    className={cn(
                      "text-sm font-medium",
                      isSelected
                        ? "text-coval-info-on-surface"
                        : "text-coval-text",
                    )}
                  >
                    {item.label}
                  </div>
                  <div
                    className={cn(
                      "mt-1 text-xs leading-relaxed",
                      isSelected
                        ? "text-coval-info-on-surface"
                        : "text-coval-text-muted",
                    )}
                  >
                    {item.description}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

interface AgentTypeTriggerProps {
  icon?: LucideIcon
  label: string
  description?: string
  onClick: () => void
}

/** Shows the chosen agent type and opens the type modal to change it. */
export function AgentTypeTrigger({
  icon: Icon,
  label,
  description,
  onClick,
}: AgentTypeTriggerProps) {
  return (
    <button
      type="button"
      aria-label="Change agent type"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border border-coval-border bg-surface-default p-3 text-left transition-colors hover:bg-surface-default-hover",
        "focus-visible:outline-none",
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0 text-coval-text-muted" />}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-coval-text">
          {label || "Choose an agent type"}
        </div>
        {description && (
          <div className="mt-0.5 text-xs leading-relaxed text-coval-text-muted">
            {description}
          </div>
        )}
      </div>
      <ChevronDownIcon />
    </button>
  )
}

export function AgentTypeModal({
  isOpen,
  onClose,
  onSelect,
  groups = AGENT_TYPE_GROUPS,
  currentValue,
  title = "Select Agent Type",
}: AgentTypeModalProps) {
  const [query, setQuery] = useState("")
  const filteredGroups = useAvailableAgentTypeGroups(groups, query)

  useEffect(() => {
    if (isOpen) {
      setQuery("")
    }
  }, [isOpen])

  const handleSelect = (type: SimulatorType) => {
    onSelect(type)
    onClose()
  }

  // Saved agents can hold types that are no longer offered (e.g. a hidden
  // Grok agent); keep those current types surfaced as the selected option.
  const currentItem = groups
    .flatMap((group) => group.items)
    .find((item) => item.type === currentValue)
  const legacyCurrentType =
    currentValue && !currentItem ? currentValue : undefined
  const LegacyCurrentIcon = legacyCurrentType
    ? AGENT_TYPE_ICONS[legacyCurrentType]
    : undefined

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[80vh] w-full max-w-4xl"
        data-testid="agent-type-modal"
      >
        <DialogHeader className="border-b border-coval-border pb-4">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-base">{title}</DialogTitle>
            <DialogDescription className="sr-only">
              Choose the simulator type that matches how Coval connects to your
              agent.
            </DialogDescription>
            <div className="flex items-center gap-2">
              <SearchInput
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClear={() => setQuery("")}
                placeholder="Search agent types..."
                className="w-64"
              />
              <Button
                variant="secondary"
                icon
                onClick={onClose}
                tooltip="Close"
              >
                <X />
              </Button>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="overflow-y-auto p-6">
          {legacyCurrentType != null && (
            <div
              data-testid={`agent-type-option-${legacyCurrentType}`}
              className="mb-6 rounded-lg border border-coval-info-border bg-coval-info-surface p-3 text-left"
            >
              {LegacyCurrentIcon ? (
                <LegacyCurrentIcon className="mb-2 h-4 w-4 text-coval-info-on-surface" />
              ) : null}
              <div className="text-sm font-medium text-coval-info-on-surface">
                {formatSimulatorType(legacyCurrentType)}
              </div>
            </div>
          )}
          {filteredGroups.length === 0 ? (
            <div className="py-8 text-center text-sm text-coval-text-muted">
              No agent types match &ldquo;{query.trim()}&rdquo;
            </div>
          ) : (
            <AgentTypeOptionGroups
              groups={filteredGroups}
              onSelect={handleSelect}
              currentValue={currentValue}
            />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
